import React from 'react';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsAnyOverlayVisible } from 'store/slice/overlay';
import { addEventListenerToWindow, removeEventListenerFromWindow, setAttributeInTag, toggleClassNameInTag } from 'core/utils/domUtils';
import { getRootUrl } from 'core/window/bootstrapConfig';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { selectPageTypeId } from 'store/slice/navigation';
import { createSelector } from '@reduxjs/toolkit';
import { onWindowResize, selectIsMenuOpen } from 'store/slice/menu';
import { ActivitiesPageTypes, isHomePage } from 'core/constant/analytics/AnalyticsPageType';
import { setScreenSize } from 'store/slice/screen';
import { useMediaQueryContext } from '@drone-js/core';

const removeBackgroundImageForHomePage = (rootUrl: string, pageTypeId: ActivitiesPageTypes) => {
    if (isHomePage(pageTypeId)) return;
    setAttributeInTag('html', 'style', 'background-image: none;');
};

const isAnyOverlayVisibleSelector = createSelector(selectIsAnyOverlayVisible, selectIsMenuOpen, (isAnyOverlayVisible, isMenuOpen) => {
    return isAnyOverlayVisible || isMenuOpen;
});

/**
 * This is a shared component. It is expected to run on all pages.
 * Use it to create Global layout effects, register global events etc.
 */
export const ActivitiesGlobalHandler: React.FC<{ config: BootstrapConfig }> = (props) => {
    const anyOverlayVisible = useSelector(isAnyOverlayVisibleSelector);
    const pageTypeId = useSelector(selectPageTypeId);
    const rootUrl = getRootUrl(props.config);
    const dispatch = useDispatch();
    const { xl, l, m } = useMediaQueryContext();

    const updateWidthWithDebounce = debounce(() => dispatch(onWindowResize()), 300);

    React.useEffect(() => {
        dispatch(onWindowResize());
    }, [dispatch]);

    React.useEffect(() => {
        dispatch(setScreenSize({ xl, l, m }));
    }, [xl, l, m, dispatch]);

    React.useEffect(() => {
        addEventListenerToWindow('resize', updateWidthWithDebounce);
        return () => removeEventListenerFromWindow('resize', updateWidthWithDebounce);
    }, [updateWidthWithDebounce]);

    toggleClassNameInTag('body', 'any-overlay-visible', anyOverlayVisible);
    removeBackgroundImageForHomePage(rootUrl, pageTypeId);

    return null;
};
ActivitiesGlobalHandler.displayName = 'ActivitiesGlobalHandler';
